import { createContext, useContext } from "react";
import type {
    OverlayCloseReason,
    OverlayEntry,
    OverlayHandle,
    OverlayOpenOptions,
    OverlayRuntimeValue,
    OverlaySurface,
    WindowPlacement,
} from "./types";

/** Аргументы открытия overlay через provider. */
export interface OpenOverlayArgs extends OverlayOpenOptions {
    surface: OverlaySurface;
    placement?: WindowPlacement;
    render: () => React.ReactElement;
    resolveValue?: (value: unknown) => void;
}

/** Контекст overlay-провайдера. */
export interface OverlayContextValue {
    entries: OverlayEntry[];
    openOverlay: (args: OpenOverlayArgs) => OverlayHandle;
    closeOverlay: (id: string, reason?: OverlayCloseReason) => void;
    closeOverlaysByOwner: (ownerId: string) => void;
    focusOverlay: (id: string) => void;
    isOverlayOpen: (id: string) => boolean;
    isOverlayTopmost: (id: string) => boolean;
    registerSurfaceElement: (id: string, element: HTMLElement | null) => void;
}

export const OverlayContext = createContext<OverlayContextValue | undefined>(undefined);

/**
 * Доступ к API overlay-провайдера.
 */
export function useOverlayContext(): OverlayContextValue {
    const ctx = useContext(OverlayContext);
    if (ctx == null) {
        throw new Error("useOverlayContext must be used within OverlayProvider");
    }
    return ctx;
}

export const OverlayRuntimeContext = createContext<OverlayRuntimeValue | undefined>(undefined);

/**
 * Runtime API текущей overlay-surface (внутри window/dialog content).
 */
export function useOverlayRuntime(): OverlayRuntimeValue {
    const ctx = useContext(OverlayRuntimeContext);
    if (ctx == null) {
        throw new Error("useOverlayRuntime must be used inside an overlay surface");
    }
    return ctx;
}
